import {
	Alert,
	Badge,
	Box,
	Button,
	Heading,
	Icons,
	Inline,
	Spinner,
	Stack,
	Text,
	Time,
} from "@sembark-travel/ui-base"
import { ButtonLink } from "@sembark-travel/ui-router"
import { useXHR } from "@sembark-travel/xhr"
import { useState } from "react"
import useSWR from "swr"
import { MailMessageBody } from "./MailMessageBody"
import { type TMailMessage } from "./List"

type TMailThreadLinkType = "trip_plan_requests" | "trips" | "hotel_bookings"

/**
 * Collapsed-by-default body for a linked message. The listing payload
 * carries only the snippet, so opening fetches the full message (which
 * also rehydrates a cold body server-side) and marks it as read once.
 */
function CollapsibleMessageBody({
	message,
	onChange,
}: {
	message: TMailMessage
	onChange?: () => unknown
}) {
	const xhr = useXHR()
	const [open, setOpen] = useState(false)
	// Local read state so the unread styling/note update as soon as the
	// message is opened (the listing isn't refetched here).
	const [read, setRead] = useState(message.is_read !== false)
	const { data: detail, isLoading } = useSWR<TMailMessage>(
		open ? `linked-mail-detail/${message.id}` : null,
		() =>
			xhr
				.get<{ data: TMailMessage }>(`/mail-messages/${message.id}`)
				.then((resp) => resp.data.data)
	)

	function handleToggle() {
		const next = !open
		setOpen(next)
		// Mark as read on first open, only when it isn't already read
		// (fire and forget).
		if (next && !read) {
			setRead(true)
			xhr
				.post(`/mail-messages/${message.id}/mark-read`)
				.then(() => onChange?.())
				.catch(() => undefined)
		}
	}

	const full = detail || message
	const isUnread = !read

	const createdBy = message.created_by || detail?.created_by

	return (
		<Box
			borderLeftWidth="2"
			borderLeftColor={isUnread ? "accent_emphasis" : "transparent"}
			paddingLeft="3"
		>
			<Stack gap="3">
				<Stack
					justifyContent="between"
					alignItems="start"
					flexWrap="wrap"
					gap="2"
				>
					<Text fontWeight={isUnread ? "semibold" : "normal"}>
						{message.subject || message.threads?.[0]?.subject || "(No Subject)"}
					</Text>
					<Inline flexWrap="wrap" gap="2">
						{message.received_at || message.sent_at ? (
							<Text color="muted">
								<Time
									timeFormat
									dateFormat="DD MMM"
									timestamp={message.received_at || message.sent_at || ""}
								/>
							</Text>
						) : null}
						{message.direction === "outbound" ? (
							<Badge fullRounded outlined>
								<Icons.Reply size="3" /> Sent
								{createdBy ? ` by ${createdBy.name}` : null}
							</Badge>
						) : null}
					</Inline>
				</Stack>
				<Stack gap="1">
					<Inline>
						<Button size="sm" level="tertiary" onClick={handleToggle}>
							<Icons.ChevronDown rotate={open ? "180" : undefined} />{" "}
							{open ? "Hide email" : "View email"}
						</Button>
					</Inline>
					{isUnread ? (
						<Text fontSize="sm" color="muted">
							Opening this email will mark it as read.
						</Text>
					) : null}
				</Stack>
				{open ? (
					isLoading && !detail ? (
						<Spinner alignCenter />
					) : (
						<MailMessageBody
							htmlBody={full.latest_html_body || full.html_body}
							textBody={full.latest_text_body || full.text_body}
							quotedHtmlBody={full.quoted_html_body}
							quotedTextBody={full.quoted_text_body}
							messageLabel="linked email"
							hideViewToggle
							bodyAvailable={full.body_available}
							preview={full.preview}
						/>
					)
				) : null}
			</Stack>
		</Box>
	)
}

export function LatestLinkedMailMessage({
	modelType,
	modelId,
	title = "Emails",
	viewAllUrl,
	onChange,
}: {
	modelType: TMailThreadLinkType
	modelId: string | number
	title?: string
	viewAllUrl?: string
	onChange?: () => unknown
}) {
	const xhr = useXHR()
	const { data, isLoading } = useSWR<{ data: TMailMessage[] }>(
		`latest-linked-mail-message/${modelType}/${modelId}`,
		() =>
			xhr
				.get("/mail-messages", {
					params: {
						model_type: modelType,
						model_id: modelId,
						page: 1,
						limit: 1,
					},
				})
				.then((resp) => resp.data)
	)
	const message = data?.data?.[0]

	return (
		<Stack borderWidth="1" rounded="md">
			<Inline
				justifyContent="between"
				alignItems="center"
				paddingX="4"
				paddingY="2"
				bgColor="subtle"
				roundedTop="md"
				borderBottomWidth="1"
				gap="3"
			>
				<Heading fontSize="md">{title}</Heading>
				{viewAllUrl ? (
					<ButtonLink size="sm" level="tertiary" to={viewAllUrl}>
						View All <Icons.ChevronDown rotate="270" />
					</ButtonLink>
				) : null}
			</Inline>
			<Box padding="4" bgColor="default">
				{isLoading ? (
					<Spinner alignCenter />
				) : !message ? (
					<Alert status="info">
						No email messages are linked to this record yet.
					</Alert>
				) : (
					<CollapsibleMessageBody message={message} onChange={onChange} />
				)}
			</Box>
		</Stack>
	)
}
